import { hasCameraKeys } from '../renderer/src/camera-keyframes.js';
import { requiresPaidGimbal, type GimbalUsageInput, type GimbalUsageContext } from './camera-gimbal-usage.js';
import { cinematicUsesDepthOfField, type CinematicSequence } from '../renderer/src/cinematic-sequence.js';
import type { DepthOfFieldSettings } from '../renderer/src/depth-of-field.js';
import { createSocialPresentation } from '../renderer/src/social-presentation.js';
import { createVideoPresentation, type VideoPresentation } from '../renderer/src/video-presentation.js';

export const STUDIO_PRO_FEATURES = ['depth-of-field', 'fixed-camera-angle', 'custom-appearance', 'camera-gimbal', 'camera-animation'] as const;
export type StudioProFeature = (typeof STUDIO_PRO_FEATURES)[number];
export type StudioFeatureCapabilities = Record<StudioProFeature, { preview: boolean; export: boolean }>;
export const STUDIO_FEATURE_LABELS: Record<StudioProFeature, string> = {
  'camera-gimbal': 'Camera Gimbal', 'camera-animation': 'Camera animation',
  'depth-of-field': 'Depth of field', 'fixed-camera-angle': 'Fixed camera angle', 'custom-appearance': 'Custom text and overlays',
};

export function studioFeatureCapabilities(kind: 'trial' | 'paid' | 'admin' | 'none', active = true): StudioFeatureCapabilities {
  const permitted = active && (kind === 'paid' || kind === 'admin');
  return Object.fromEntries(STUDIO_PRO_FEATURES.map(feature => [feature, { preview: true, export: permitted }])) as StudioFeatureCapabilities;
}

/** Restores only paid finishing fields, retaining layout and information visibility. */
export function resetProAppearance(presentation: VideoPresentation): VideoPresentation {
  if (presentation.version === 2) {
    const defaults = createSocialPresentation(presentation.target, presentation.layout);
    return { ...presentation, titleSize: defaults.titleSize, statisticsSize: defaults.statisticsSize,
      extendedNumeralScale: defaults.extendedNumeralScale, textColor: defaults.textColor,
      upperVerticalOffset: defaults.upperVerticalOffset, lowerVerticalOffset: defaults.lowerVerticalOffset,
      plateTransparency: defaults.plateTransparency };
  }
  const defaults = createVideoPresentation(presentation.target, presentation.layout);
  return { ...presentation, titlePosition: defaults.titlePosition, titleScale: defaults.titleScale, statisticsScale: defaults.statisticsScale,
    ...(presentation.plateTransparency === undefined ? {} : { plateTransparency: 0 }),
    ...(presentation.textColor === undefined ? {} : { textColor: '#ffffff' }) };
}

export function requiredStudioFeatures(output: {
  depthOfField?: DepthOfFieldSettings | undefined; cameraAngle?: number | undefined;
  presentation?: VideoPresentation | undefined; cinematicSequence?: CinematicSequence | undefined;
} & GimbalUsageInput, gimbalContext?: GimbalUsageContext): StudioProFeature[] {
  const required: StudioProFeature[] = [];
  if (requiresPaidGimbal(output, gimbalContext)) required.push('camera-gimbal');
  if (output.depthOfField?.enabled || cinematicUsesDepthOfField(output.cinematicSequence)) required.push('depth-of-field');
  if (hasCameraKeys(output.cinematicSequence?.cameraTracks)) required.push('camera-animation');
  if (output.cameraAngle !== undefined || output.cinematicSequence?.waypoints.some(item => item.overrides.cameraAngle != null)) required.push('fixed-camera-angle');
  if (output.presentation) {
    const reset = resetProAppearance(output.presentation);
    if (JSON.stringify(reset) !== JSON.stringify(output.presentation)) required.push('custom-appearance');
  }
  const appearanceBase = output.presentation ?? createVideoPresentation('none');
  if (!required.includes('custom-appearance') && output.cinematicSequence?.waypoints.some(item => {
    const settings = item.overrides.presentationAppearance;
    if (!settings) return false;
    const value = { ...appearanceBase, ...settings } as VideoPresentation;
    return JSON.stringify(resetProAppearance(value)) !== JSON.stringify(value);
  })) required.push('custom-appearance');
  return required;
}

export function unavailableStudioFeatures(required: readonly StudioProFeature[], capabilities?: StudioFeatureCapabilities | undefined) {
  return required.filter(feature => capabilities?.[feature]?.export !== true);
}
